/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/extractSourceUrls.ts
 * PURPOSE: Pull http(s) URLs from assistant markdown/plain text for Perplexity-style “Sources” chips.
 */

const TRAILING_JUNK = /[),.;:!?'"\]}>]+$/;

function normalizeUrl(raw: string): string | null {
  let s = raw.trim();
  if (!/^https?:\/\//i.test(s)) return null;
  s = s.replace(TRAILING_JUNK, '');
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.href;
  } catch {
    return null;
  }
}

/**
 * Ordered unique http(s) URLs (markdown links, autolinks, bare URLs). Caps at `max`.
 */
export function extractSourceUrls(text: string, max = 8): string[] {
  if (!text.trim()) return [];
  const seen = new Set<string>();
  const out: string[] = [];

  const push = (raw: string) => {
    const u = normalizeUrl(raw);
    if (!u || seen.has(u)) return;
    seen.add(u);
    out.push(u);
  };

  const mdLink = /\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/gi;
  let m: RegExpExecArray | null;
  while ((m = mdLink.exec(text)) !== null) {
    push(m[2]);
    if (out.length >= max) return out;
  }

  const angle = /<(https?:\/\/[^>\s]+)>/gi;
  while ((m = angle.exec(text)) !== null) {
    push(m[1]);
    if (out.length >= max) return out;
  }

  const bare = /(https?:\/\/[^\s<>"'`]+)/gi;
  while ((m = bare.exec(text)) !== null) {
    push(m[1]);
    if (out.length >= max) return out;
  }

  return out;
}

export function shortHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url.slice(0, 32);
  }
}
